import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  MapPin, 
  Phone, 
  Calendar,
  DollarSign,
  Users,
  TrendingUp,
  Clock,
  Award,
  CreditCard,
  Package,
  Star,
  StickyNote,
  Plus,
  Eye,
  EyeOff,
  Trash2,
  MessageSquare
} from 'lucide-react';
import { LogOutcomeDialog } from '@/components/admin/LogOutcomeDialog';
import { OutcomeHistory } from '@/components/admin/OutcomeHistory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers from localStorage or sessionStorage
const getAuthHeaders = () => {
  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
};

export default function AdminPartnerDetail() {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [samplesSaving, setSamplesSaving] = useState(false);
  const [ratingSaving, setRatingSaving] = useState(false);
  const [hoverRating, setHoverRating] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({ body: '', visible_to_partner: false, kind: 'meeting' });
  const [noteSaving, setNoteSaving] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [outcomeKey, setOutcomeKey] = useState(0);

  const setRating = (r) => {
    if (!data?.partner) return;
    setRatingSaving(true);
    const xhr = new XMLHttpRequest();
    xhr.open('PATCH', `${API}/admin/partners/${partnerId}/rating`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(getAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setRatingSaving(false);
      if (xhr.status === 200) {
        setData(d => ({ ...d, partner: { ...d.partner, star_rating: r } }));
        toast.success(`Rated ${r}/5`);
      } else toast.error('Failed to save rating');
    };
    xhr.onerror = () => { setRatingSaving(false); toast.error('Network error'); };
    xhr.send(JSON.stringify({ rating: r }));
  };

  const fetchNotes = () => {
    setNotesLoading(true);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/admin/partners/${partnerId}/notes`, true);
    Object.entries(getAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setNotesLoading(false);
      if (xhr.status === 200) setNotes(JSON.parse(xhr.responseText).notes || []);
    };
    xhr.send();
  };

  const submitNote = () => {
    if (!noteForm.body.trim()) return toast.error('Note body is required');
    setNoteSaving(true);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/admin/partners/${partnerId}/notes`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(getAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setNoteSaving(false);
      if (xhr.status === 200) {
        toast.success('Note added');
        setNoteOpen(false);
        setNoteForm({ body: '', visible_to_partner: false, kind: 'meeting' });
        fetchNotes();
      } else toast.error('Failed to add note');
    };
    xhr.onerror = () => { setNoteSaving(false); toast.error('Network error'); };
    xhr.send(JSON.stringify(noteForm));
  };

  const toggleNoteVisibility = (noteId, newVal) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PATCH', `${API}/admin/partners/${partnerId}/notes/${noteId}/visibility`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(getAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      if (xhr.status === 200) {
        setNotes(ns => ns.map(n => n.note_id === noteId ? { ...n, visible_to_partner: newVal } : n));
        toast.success(newVal ? 'Now visible to partner' : 'Hidden from partner');
      }
    };
    xhr.send(JSON.stringify({ visible_to_partner: newVal }));
  };

  const deleteNote = (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    const xhr = new XMLHttpRequest();
    xhr.open('DELETE', `${API}/admin/partners/${partnerId}/notes/${noteId}`, true);
    Object.entries(getAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      if (xhr.status === 200) {
        setNotes(ns => ns.filter(n => n.note_id !== noteId));
        toast.success('Note deleted');
      }
    };
    xhr.send();
  };

  const toggleSamplesReceived = (received) => {
    if (!data?.partner) return;
    setSamplesSaving(true);
    const xhr = new XMLHttpRequest();
    xhr.open('PATCH', `${API}/admin/partners/${partnerId}/samples-received`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(getAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setSamplesSaving(false);
      if (xhr.status === 200) {
        toast.success(received ? 'Marked as received' : 'Unmarked');
        // Optimistic update
        setData(d => ({ ...d, partner: { ...d.partner, samples_received: received, samples_received_at: received ? new Date().toISOString() : null } }));
      } else {
        toast.error('Failed to update');
      }
    };
    xhr.onerror = () => { setSamplesSaving(false); toast.error('Network error'); };
    xhr.send(JSON.stringify({ received }));
  };

  useEffect(() => {
    fetchPartnerDetail();
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  const handleRebuildBalance = async () => {
    if (!confirm('Recompute this partner\'s commission balance from their transactions?\n\nUse this if the partner reports ₹0 on their dashboard while you see a balance here. This rewrites the user record\'s total/pending commission from the authoritative transactions data.')) {
      return;
    }
    setRebuilding(true);
    try {
      const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const r = await fetch(`${API}/admin/debug/partner-dashboard/${partnerId}/rebuild-balance`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Rebuild failed');
      toast.success(
        `Balance rebuilt: total ₹${(d.old_total_commission || 0).toFixed(2)} → ₹${d.new_total_commission.toFixed(2)}, pending ₹${(d.old_pending_commission || 0).toFixed(2)} → ₹${d.new_pending_commission.toFixed(2)}`
      );
      fetchPartnerDetail();
    } catch (e) {
      toast.error(e.message || 'Rebuild failed');
    } finally {
      setRebuilding(false);
    }
  };

  const fetchPartnerDetail = async () => {
    const token = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    const url = `${API}/admin/partners/${partnerId}`;
    
    setDebugInfo({ url, tokenPresent: !!token, tokenStart: token ? token.substring(0, 20) : 'none' });
    
    try {
      const response = await fetch(url, { headers });
      
      // Clone response so we can read it twice if needed
      const responseClone = response.clone();
      
      setDebugInfo(prev => ({ ...prev, status: response.status }));
      
      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await responseClone.text();
        } catch (e) {
          errorText = 'Could not read error response';
        }
        setDebugInfo(prev => ({ ...prev, error: `HTTP ${response.status}: ${errorText}` }));
        throw new Error(`HTTP ${response.status}`);
      }
      
      const result = await response.json();
      setDebugInfo(prev => ({ ...prev, dataReceived: true }));
      setData(result);
    } catch (error) {
      toast.error('Failed to load partner details');
      setDebugInfo(prev => ({ ...prev, fetchError: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'platinum':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'gold':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' 
      ? 'bg-green-100 text-green-700 border-green-200' 
      : 'bg-red-100 text-red-700 border-red-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold mb-2">Partner not found</h2>
        <p className="text-muted-foreground mb-4">Partner ID: {partnerId}</p>
        {debugInfo && (
          <div className="bg-gray-100 p-4 rounded-lg text-left max-w-md mx-auto mb-4 text-sm">
            <p><strong>Debug Info:</strong></p>
            <p>URL: {debugInfo.url}</p>
            <p>Token Present: {debugInfo.tokenPresent ? 'Yes' : 'No'}</p>
            <p>Token Start: {debugInfo.tokenStart}</p>
            <p>Response Status: {debugInfo.status || 'N/A'}</p>
            <p>Error: {debugInfo.error || debugInfo.fetchError || 'None'}</p>
            <p>Data Received: {debugInfo.dataReceived ? 'Yes' : 'No'}</p>
          </div>
        )}
        <Button onClick={() => navigate('/admin/partners')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Partners
        </Button>
      </div>
    );
  }

  const { partner, stats, recent_referrals, recent_transactions, recent_payouts } = data;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/admin/partners')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Partner Profile
          </h1>
        </div>
      </div>

      {/* Partner Info Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
              <AvatarImage src={partner.picture} />
              <AvatarFallback className="text-3xl bg-primary/10 text-primary">
                {partner.name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl font-bold">{partner.name}</h2>
                <Badge className={getTierColor(partner.tier || 'silver')}>
                  <Award className="h-3 w-3 mr-1" />
                  {partner.tier?.toUpperCase() || 'SILVER'}
                </Badge>
                <Badge className={getStatusColor(partner.status || 'active')}>
                  {partner.status || 'active'}
                </Badge>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <span>{partner.email}</span>
                </div>
                {partner.location && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span><strong>{partner.location}</strong></span>
                  </div>
                )}
                {partner.city && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>{partner.city}</span>
                  </div>
                )}
                {partner.phone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span>{partner.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Joined {new Date(partner.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Samples received switch */}
              <div className="flex items-center justify-between gap-3 mt-4 p-3 rounded-lg bg-muted/40 border" data-testid="samples-received-row">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-primary" />
                  <div>
                    <div className="text-sm font-medium">Samples received</div>
                    <div className="text-xs text-muted-foreground">
                      {partner.samples_received
                        ? (partner.samples_received_at ? `Yes · marked on ${new Date(partner.samples_received_at).toLocaleDateString()}` : 'Yes')
                        : 'Not yet — partner hasn\'t confirmed tasting the products'}
                    </div>
                  </div>
                </div>
                <Switch
                  checked={!!partner.samples_received}
                  onCheckedChange={toggleSamplesReceived}
                  disabled={samplesSaving}
                  data-testid="samples-received-switch"
                />
              </div>

              {/* Star rating */}
              <div className="flex items-center justify-between gap-3 mt-3 p-3 rounded-lg bg-muted/40 border" data-testid="star-rating-row">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-500" />
                  <div>
                    <div className="text-sm font-medium">Influence rating</div>
                    <div className="text-xs text-muted-foreground">
                      {partner.star_rating ? `${partner.star_rating}/5 stars` : 'Not rated — how influential is this partner?'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-0.5" onMouseLeave={() => setHoverRating(null)}>
                  {[1, 2, 3, 4, 5].map(n => {
                    const filled = (hoverRating ?? partner.star_rating ?? 0) >= n;
                    return (
                      <button
                        key={n}
                        onMouseEnter={() => setHoverRating(n)}
                        onClick={() => setRating(n)}
                        disabled={ratingSaving}
                        data-testid={`star-${n}`}
                        title={`${n} star${n === 1 ? '' : 's'}`}
                        className="p-0.5 hover:scale-110 transition disabled:opacity-50"
                      >
                        <Star className={`w-6 h-6 ${filled ? 'fill-yellow-400 text-yellow-500' : 'text-gray-300'}`} />
                      </button>
                    );
                  })}
                  {partner.star_rating ? (
                    <button
                      onClick={() => setRating(0)}
                      disabled={ratingSaving}
                      data-testid="star-clear"
                      className="ml-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
              </div>
              
              <div className="pt-2">
                <p className="text-sm text-muted-foreground">Referral Code</p>
                <p className="font-mono font-bold text-lg text-primary">{partner.referral_code}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Outreach / re-engagement log */}
      <Card data-testid="outreach-card">
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-orange-500" />
              <CardTitle>Outreach log</CardTitle>
            </div>
            <Button size="sm" onClick={() => setOutcomeOpen(true)} data-testid="profile-log-outcome-btn" className="bg-orange-500 hover:bg-orange-600 text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Log interaction
            </Button>
          </div>
          <CardDescription>Log a call outcome or comment any time — even if this partner isn't in today's re-engagement list. Usual queue cooldown rules apply.</CardDescription>
        </CardHeader>
        <CardContent>
          <OutcomeHistory partnerId={partnerId} refreshKey={outcomeKey} />
        </CardContent>
      </Card>
      <LogOutcomeDialog open={outcomeOpen} onOpenChange={setOutcomeOpen} partner={{ ...partner, user_id: partner.user_id || partnerId }}
        onSaved={(_, payload) => {
          setOutcomeKey(k => k + 1);
          if (payload?.star_rating) setData(d => ({ ...d, partner: { ...d.partner, star_rating: payload.star_rating } }));
        }} />

      {/* Notes timeline */}
      <Card data-testid="notes-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-primary" />
              <CardTitle>Meeting notes & conversation history</CardTitle>
            </div>
            <Button size="sm" onClick={() => setNoteOpen(true)} data-testid="add-note-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add note
            </Button>
          </div>
          <CardDescription>Internal notes stay private. Toggle "Share with partner" to make a note visible on the partner's own dashboard.</CardDescription>
        </CardHeader>
        <CardContent>
          {notesLoading ? (
            <div className="text-sm text-muted-foreground">Loading...</div>
          ) : notes.length === 0 ? (
            <div className="text-sm text-muted-foreground p-4 text-center">No notes yet. Click "Add note" to log a meeting or signup conversation.</div>
          ) : (
            <div className="space-y-3">
              {notes.map(n => (
                <div key={n.note_id} className="p-3 border rounded-md" data-testid={`note-${n.note_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="outline" className="text-xs capitalize">{n.kind}</Badge>
                        {n.visible_to_partner && (
                          <Badge className="bg-green-100 text-green-700 border-green-200 text-xs" variant="outline">
                            <Eye className="w-3 h-3 mr-1" /> Shared with partner
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {n.author_email} · {new Date(n.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-sm whitespace-pre-wrap">{n.body}</div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => toggleNoteVisibility(n.note_id, !n.visible_to_partner)}
                        title={n.visible_to_partner ? 'Hide from partner' : 'Share with partner'}
                        data-testid={`note-toggle-${n.note_id}`}
                        className="p-1 text-muted-foreground hover:text-foreground"
                      >
                        {n.visible_to_partner ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => deleteNote(n.note_id)}
                        title="Delete"
                        data-testid={`note-delete-${n.note_id}`}
                        className="p-1 text-muted-foreground hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add note dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add note for {partner.name || partner.email}</DialogTitle>
            <DialogDescription>Capture what was discussed. If you want the partner to see this when they log in, toggle "Share with partner".</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Note type</Label>
              <div className="flex gap-2 mt-2">
                {['signup', 'meeting', 'general'].map(k => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setNoteForm({ ...noteForm, kind: k, visible_to_partner: k === 'signup' })}
                    data-testid={`note-kind-${k}`}
                    className={`px-3 py-1 text-sm rounded border capitalize ${noteForm.kind === k ? 'bg-primary text-primary-foreground border-primary' : ''}`}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm">What was discussed? *</Label>
              <Textarea
                rows={5}
                value={noteForm.body}
                onChange={e => setNoteForm({ ...noteForm, body: e.target.value })}
                placeholder="e.g. Introduced the partnership structure, discussed their patient demographic, confirmed interest in the mango whey line..."
                data-testid="note-body-input"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-md bg-muted/40 border">
              <div>
                <div className="text-sm font-medium">Share with partner</div>
                <div className="text-xs text-muted-foreground">If on, the partner sees this note on their own dashboard.</div>
              </div>
              <Switch
                checked={noteForm.visible_to_partner}
                onCheckedChange={(v) => setNoteForm({ ...noteForm, visible_to_partner: v })}
                data-testid="note-visibility-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNoteOpen(false)}>Cancel</Button>
            <Button onClick={submitNote} disabled={noteSaving || !noteForm.body.trim()} data-testid="note-submit">
              {noteSaving ? 'Saving...' : 'Save note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Commission</CardTitle>
              <DollarSign className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">₹{stats.total_commission?.toFixed(2) || '0.00'}</div>
              <p className="text-xs text-muted-foreground">₹{stats.pending_commission?.toFixed(2) || '0.00'} pending</p>
              <button
                type="button"
                onClick={handleRebuildBalance}
                disabled={rebuilding}
                className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                title="Recompute this partner's balance from transactions. Use if the partner reports ₹0 on their dashboard while here you see a balance."
                data-testid="rebuild-balance-button"
              >
                {rebuilding ? 'Rebuilding...' : 'Rebuild balance from transactions'}
              </button>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Referrals</CardTitle>
              <Users className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_referrals || 0}</div>
              <p className="text-xs text-muted-foreground">
                {stats.active_referrals || 0} active, {stats.pending_referrals || 0} pending
                {stats.paid_referrals > 0 && `, ${stats.paid_referrals} paid`}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
              <TrendingUp className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">₹{stats.total_revenue?.toFixed(2) || '0.00'}</div>
              <p className="text-xs text-muted-foreground">from all referrals</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Tenure</CardTitle>
              <Clock className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.tenure_months || 0} months</div>
              <p className="text-xs text-muted-foreground">{stats.tier?.commission_rate * 100 || 15}% commission rate</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Bank Details */}
      {(partner.bank_name || partner.account_number) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Bank Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Bank Name</p>
                <p className="font-medium">{partner.bank_name || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Account Number</p>
                <p className="font-medium">
                  {partner.account_number ? `****${partner.account_number.slice(-4)}` : 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Account Holder</p>
                <p className="font-medium">{partner.account_holder || 'Not provided'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* All Referrals */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              All Referrals ({recent_referrals?.length || 0})
            </CardTitle>
            <div className="flex gap-2 text-sm">
              <Badge variant="secondary">
                Active: {recent_referrals?.filter(r => r.status === 'active').length || 0}
              </Badge>
              <Badge variant="outline">
                Pending: {recent_referrals?.filter(r => r.status === 'pending').length || 0}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {recent_referrals?.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recent_referrals.map((ref) => (
                  <div 
                    key={ref.referral_id} 
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      ref.status === 'active' ? 'bg-blue-50/50 border-blue-200' :
                      ref.status === 'paid' ? 'bg-green-50/50 border-green-200' :
                      ref.status === 'expired' ? 'bg-gray-50/50 border-gray-200' :
                      'bg-orange-50/50 border-orange-200'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{ref.customer_name}</p>
                        {ref.auto_created && (
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">
                            Auto
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {ref.customer_email || ref.customer_phone || 'No contact'}
                      </p>
                      {ref.auto_created_from_order && (
                        <p className="text-xs text-purple-600">From order {ref.auto_created_from_order}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <Badge 
                        variant={ref.status === 'paid' ? 'default' : 'secondary'}
                        className={
                          ref.status === 'active' ? 'bg-blue-100 text-blue-700' :
                          ref.status === 'paid' ? 'bg-green-100 text-green-700' :
                          ref.status === 'expired' ? 'bg-gray-100 text-gray-700' :
                          'bg-orange-100 text-orange-700'
                        }
                      >
                        {ref.status}
                      </Badge>
                      <p className="text-sm font-semibold text-green-600 mt-1">
                        ₹{(ref.total_commission_earned || ref.commission_amount || 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(ref.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No referrals yet</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            {recent_transactions?.length > 0 ? (
              <div className="space-y-3">
                {recent_transactions.map((txn) => (
                  <div key={txn.transaction_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">{txn.customer_name || 'Customer'}</p>
                      <p className="text-xs text-muted-foreground">
                        Order: {txn.order_number ? `KBT${txn.order_number}` : txn.order_id}
                        {txn.transaction_date && (
                          <span className="ml-2">
                            · {new Date(txn.transaction_date).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">₹{txn.amount?.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">
                        Commission: ₹{txn.commission_amount?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No transactions yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Payouts */}
      <Card>
        <CardHeader>
          <CardTitle>Payout History</CardTitle>
        </CardHeader>
        <CardContent>
          {recent_payouts?.length > 0 ? (
            <div className="space-y-3">
              {recent_payouts.map((payout) => (
                <div key={payout.payout_id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-semibold">₹{payout.amount?.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(payout.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant={payout.status === 'completed' ? 'default' : payout.status === 'pending' ? 'secondary' : 'destructive'}>
                    {payout.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No payouts yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
